import { LinkProps } from '@mui/material/Link';
import { LinkBehavior } from './LinkBehaviour';
// this import is required for datagrid theming
import type {} from '@mui/x-data-grid/themeAugmentation';
// this import is required for date picker theming
import type {} from '@mui/x-date-pickers/themeAugmentation';
import { createTheme } from '@alterdomus-analytics/dna-ui';
import type { ThemeOptions } from '@mui/material';

const themeValues = {
  primary: {
    main: '#162859',
    light: '#1628591a',
    dark: '#0A1638',
    background: '#F1F5F5' //light1
  },
  secondary: {
    main: '#1A50DD',
    light: '#C9F2F2',
    dark: '#0C276C',
    background: '#E8EEEE' //light2
  },
  info: {
    main: '#51B0AC',
    light: '#E7EEEE',
    dark: '#146360',
    background: '#DEE9E8' //light3
  },
  success: {
    main: '#538742',
    light: '#53874233',
    dark: '#2A5B1A',
    background: '#323030' //dark1
  },
  warning: {
    main: '#E1B92B',
    light: '#FDEBBE',
    dark: '#8F7518',
    background: '#454545' //dark2
  },
  error: {
    main: '#9F372F',
    light: '#F9DDDB',
    dark: '#67150E'
  },
  custom: {
    palette: {
      grey: '#EAECF0',
      dark2: '#454545',
      white: '#FFFFFF',
      infoLightDarkened: '#CBD1D1',
      grey300: '#D0D5DD',
      grey600: '#475467',
      greenGrey: '#586463',
      lightGrey: '#A1A5A5',
      blueGrey: '#F2F4F7',
      infoLight50: '#E7EEEE80',
      activeBack: '#DDE7D9',
      activeFore: '#2A5B19',
      progressBack: '#D9E2E2',
      dueSoonFore: '#5C543E',
      light1: '#F1F5F5',
      light2: '#E8EEEE',
      light3: '#DEE9E8',
      accentBackground: '#C6D7D5',
      darkGrey: '#313636',
      disabledBackgroundColor: '#16285926',
      disabledColor: '#16285973',
      navyBlue: '#16285914',
      darkText: '#292727'
    }
  }
};

const theme = createTheme({
  palette: {
    primary: themeValues.primary,
    secondary: themeValues.secondary,
    info: themeValues.info,
    success: themeValues.success,
    warning: themeValues.warning,
    error: themeValues.error
  },
  typography: {
    fontFamily: 'Inter',
    subtitle3: {
      color: themeValues.custom.palette.greenGrey,
      fontSize: '12px',
      fontFamily: 'Inter',
      fontWeight: '400',
      lineHeight: '16px'
    },
    h1: {
      fontSize: '36px',
      fontFamily: 'Inter',
      fontWeight: '300',
      lineHeight: '44px'
    },
    h2: {
      fontSize: '28px',
      fontFamily: 'Inter',
      fontWeight: '300',
      lineHeight: '36px'
    },
    h3: {
      fontSize: '24px',
      fontFamily: 'Inter',
      fontWeight: '300',
      lineHeight: '32px'
    },
    h4: {
      fontSize: '22px',
      fontFamily: 'Inter',
      fontWeight: '300',
      lineHeight: '28px'
    },
    h5: {
      fontSize: '16px',
      fontFamily: 'Inter',
      fontWeight: '500',
      lineHeight: '24px'
    },
    h6: {
      fontSize: '14px',
      fontFamily: 'Inter',
      fontWeight: '500',
      textTransform: 'uppercase',
      lineHeight: '20px'
    },
    caption: {
      // small label
      fontSize: '12px',
      fontFamily: 'Inter',
      fontWeight: '500',
      textTransform: 'uppercase',
      lineHeight: '16px'
    },
    subtitle1: {
      //body large
      fontSize: '16px',
      fontFamily: 'Inter',
      fontWeight: '400',
      lineHeight: '24px'
    },
    body1: {
      //body med
      fontSize: '14px',
      fontFamily: 'Inter',
      fontWeight: '400',
      lineHeight: '20px'
    },
    body2: {
      //body small
      fontSize: '12px',
      fontFamily: 'Inter',
      fontWeight: '400',
      lineHeight: '16px'
    }
  },
  custom: themeValues.custom,
  components: {
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '4px',
          textTransform: 'uppercase',
          fontSize: '12px',
          fontWeight: '500',
          '& .MuiChip-label': {
            padding: '2px 4px'
          }
        }
      },
      variants: [
        {
          props: { variant: 'assigned' },
          style: {
            backgroundColor: themeValues.secondary.main,
            color: themeValues.custom.palette.white
          }
        },
        {
          props: { variant: 'active' },
          style: {
            backgroundColor: themeValues.custom.palette.activeBack,
            color: themeValues.custom.palette.activeFore
          }
        },
        {
          props: { variant: 'inProgress' },
          style: {
            backgroundColor: themeValues.custom.palette.progressBack,
            color: themeValues.custom.palette.greenGrey
          }
        },
        {
          props: { variant: 'dueSoon' },
          style: {
            backgroundColor: themeValues.warning.light,
            color: themeValues.custom.palette.greenGrey
          }
        },
        {
          props: { variant: 'overdue' },
          style: {
            backgroundColor: themeValues.error.light,
            color: themeValues.error.dark
          }
        }
      ]
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          fontWeight: '400',
          '&:hover': {
            backgroundColor: themeValues.secondary.light
          },
          '&:focus': {
            backgroundColor: themeValues.info.light
          }
        }
      }
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          margin: '40px',
          border: 'none',
          '& .MuiDataGrid-main': {
            color: themeValues.custom.palette.white,
            width: '100%'
          },
          '& .MuiDataGrid-virtualScroller': {
            border: '1px solid #E0E0E0',
            borderTop: 'none',
            borderRadius: '0 0 4px 4px'
          },
          '& .MuiDataGrid-virtualScrollerRenderZone': {
            '& .MuiDataGrid-row': {
              backgroundColor: themeValues.custom.palette.white,
              fontSize: '14px',
              '&:nth-of-type(even)': { backgroundColor: '#F1F5F5' }
            }
          },
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: '#3E4545',
            color: themeValues.custom.palette.white,
            textTransform: 'uppercase',
            fontWeight: '500',
            fontSize: '12px',
            padding: '0 0 0 24px'
          },
          '& .MuiDataGrid-menuIcon': {
            display: 'none',
            '&:hover': {
              display: 'none'
            }
          },
          '& .MuiDataGrid-cell': {
            color: themeValues.custom.palette.darkText,
            padding: '0 0 0 24px',
            '&:focus-within': {
              outline: 'none'
            }
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: 'none'
          },
          '& .MuiDataGrid-iconSeparator': {
            visibility: 'hidden'
          },
          '& .MuiTablePagination-actions': {
            '& .MuiButtonBase-root': {
              color: '#313636',
              'svg:hover': {
                color: '#313636'
              }
            },
            '& .Mui-disabled': {
              color: '#31363680'
            }
          },
          '& .MuiSvgIcon-root': {
            opacity: '1 !important'
          },
          '& .MuiIconButton-root': {
            color: themeValues.custom.palette.white,
            '& :hover': {
              color: themeValues.secondary.light
            },
            '& :focus': {
              color: themeValues.secondary.light
            }
          }
        }
      }
    },
    MuiButton: {
      variants: [
        {
          props: { variant: 'contained', color: 'primary' },
          style: {
            fontWeight: '600',
            fontFamily: 'Inter',
            color: '#fff',
            backgroundColor: 'primary',
            borderRadius: '100px',
            fontSize: '12px',
            lineHeight: '20px',
            padding: '10px 24px',
            gap: '8px',
            '&:hover': {
              opacity: '0.8'
            },
            '&:focus': {
              opacity: '0.8'
            },
            '&:visited': {
              opacity: '0.8'
            },
            '&:disabled': {
              backgroundColor: '#16285926',
              color: '#16285973'
            }
          }
        },
        {
          props: { variant: 'outlined', color: 'primary' },
          style: {
            fontWeight: '600',
            fontFamily: 'Inter',
            color: 'primary',
            backgroundColor: '#fff',
            border: '#16285999 solid 1px',
            borderRadius: '100px',
            fontSize: '12px',
            lineHeight: '20px',
            padding: '10px 24px',
            gap: '8px',
            '&:hover': {
              backgroundColor: themeValues.custom.palette.navyBlue
            },
            '&:focus': {
              backgroundColor: themeValues.custom.palette.navyBlue
            },
            '&:visited': {
              backgroundColor: themeValues.custom.palette.navyBlue
            },
            '&:disabled': {
              color: '#16285961',
              border: '#16285961 1px solid'
            }
          }
        },
        {
          props: { variant: 'text', color: 'primary' },
          style: {
            fontWeight: '600',
            fontFamily: 'Inter',
            color: 'primary',
            backgroundColor: '#fff',
            borderRadius: '100px',
            fontSize: '12px',
            lineHeight: '20px',
            padding: '10px 12px',
            gap: '8px',
            '&:hover': {
              backgroundColor: themeValues.custom.palette.navyBlue
            },
            '&:focus': {
              backgroundColor: themeValues.custom.palette.navyBlue
            },
            '&:visited': {
              backgroundColor: themeValues.custom.palette.navyBlue
            },
            '&:disabled': {
              color: '#16285961'
            }
          }
        }
      ]
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: themeValues.custom.palette.darkText,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'none'
          },
          '& fieldset': {
            borderColor: themeValues.custom.palette.lightGrey
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: themeValues.secondary.main
          },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1c1b1f1f'
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: themeValues.secondary.main
          },
          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: themeValues.error.main
          }
        }
      }
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          backgroundColor: 'white',
          paddingTop: 0,
          color: themeValues.custom.palette.greenGrey,
          '&.Mui-focused': {
            color: themeValues.secondary.main,
            textTransform: 'uppercase'
          },
          '&.Mui-error.Mui-focused': {
            color: themeValues.error.main
          },
          '&.Mui-disabled': {
            color: '#26252838'
          },
          '&.MuiInputLabel-shrink': {
            textTransform: 'uppercase'
          }
        }
      }
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontSize: '12px',
          color: themeValues.custom.palette.greenGrey,
          marginLeft: '0',
          fontWeight: '400',
          '&.Mui-focused': {
            color: themeValues.secondary.main
          },
          '&.Mui-error.Mui-focused': {
            color: themeValues.error.main
          },
          '& .Mui-error': {
            color: themeValues.error.main
          }
        }
      }
    },
    MuiBadge: {
      variants: [
        {
          props: { variant: 'main' },
          style: {
            '.MuiBadge-badge': {
              borderColor: '#FFFFFF',
              borderWidth: '1px',
              borderStyle: 'solid',
              color: '#FFFFFF',
              paddingLeft: '4px',
              paddingRight: '4px',
              minWidth: '15px',
              height: '15px',
              fontSize: '8px'
            },
            '.MuiBadge-anchorOriginTopRight': {
              right: '3px',
              top: '5px'
            }
          }
        }
      ]
    },
    MuiLink: {
      defaultProps: {
        component: LinkBehavior
      } as LinkProps,
      variants: [
        {
          props: { component: 'button' },
          style: {
            color: themeValues.custom.palette.greenGrey
          }
        }
      ]
    },
    MuiSnackbar: {
      defaultProps: {
        anchorOrigin: { vertical: 'top', horizontal: 'right' }
      },
      variants: [
        {
          props: { variant: 'error' },
          style: {
            opacity: 0.8,
            '.MuiSnackbarContent-root': {
              backgroundColor: themeValues.error.dark,
              borderRadius: '6px'
            }
          }
        },
        {
          props: { variant: 'message' },
          style: {
            opacity: 0.8,
            '.MuiSnackbarContent-root': {
              backgroundColor: themeValues.custom.palette.dark2,
              borderRadius: '6px'
            }
          }
        },
        {
          props: { variant: 'success' },
          style: {
            opacity: 0.8,
            '.MuiSnackbarContent-root': {
              backgroundColor: '#015B50',
              borderRadius: '6px'
            }
          }
        }
      ]
    },
    MuiLinearProgress: {
      defaultProps: {
        color: 'secondary'
      },
      styleOverrides: {
        root: { height: 8 }
      }
    },
    MuiCircularProgress: {
      defaultProps: {
        color: 'secondary'
      }
    },
    MuiCheckbox: {
      defaultProps: {
        color: 'secondary'
      }
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&:first-of-type': {
            marginTop: 0
          },
          '&.MuiMenuItem-root': {
            padding: 0,
            '&.Mui-selected .MuiTypography-root': {
              fontWeight: 400
            },
            '&:last-of-type': {
              marginBottom: 0
            },
            '&:first-of-type': {
              marginTop: 0
            },
            '& .MuiFormControlLabel-root': {
              marginRight: 0,
              padding: '8px 20px 8px 8px'
            }
          }
        }
      }
    }
  }
} as ThemeOptions);

export default theme;
